/**
 * The canvas panel's body while the writer builds (plan 7.2), web (docked and
 * full screen). Pages get Preview and Code tabs, documents and slides the
 * Preview alone.
 *
 * - Preview, before the first snapshot: a skeleton. A page is a column at most
 *   720px wide (a 56px header bar, a 240px hero, one 160px block per section
 *   so far with at least two, a 96px footer); a document is a sheet at most
 *   760px wide with title and subtitle bars, then each heading of the outline
 *   as real text over three shimmer lines. Blocks shimmer, except under
 *   reduced motion.
 * - Each snapshot replaces the skeleton in the same frame Phase 1 previews
 *   use: an empty sandbox with no referrer, the sanitized HTML as `srcdoc`,
 *   crossfading in 150 ms (none under reduced motion).
 * - Code: CodeMirror 6, read only, loaded lazily, appending each delta and
 *   following the end until the reader scrolls up; then "Jump to latest".
 *
 * `ArtifactDraftPanel.tsx` is the native twin with the same props.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SpinnerGlyph } from '../common/SpinnerGlyph';
import {
  draftMotion,
  skeletonSectionCount,
  type ArtifactDraft,
  type ArtifactDraftPanelProps,
} from './artifact-draft-model';
import { usePrefersReducedMotion } from './building-hooks';
import type { ReadOnlyCodeView } from './artifact-codemirror.web';

export function ArtifactDraftPanel({ draft, view, onViewChange, title }: ArtifactDraftPanelProps) {
  const { t } = useTranslation('common');
  const reduceMotion = usePrefersReducedMotion();
  const motion = draftMotion(reduceMotion);
  const isPage = draft.kind === 'page';
  const current = isPage ? view : 'preview';

  return (
    <>
      {isPage ? (
        <div className="tabbio-artifact-panel-tabs" role="tablist">
          {(['preview', 'code'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={current === option}
              className={current === option ? 'tabbio-panel-tab is-selected' : 'tabbio-panel-tab'}
              onClick={() => onViewChange(option)}
            >
              {t(option === 'code' ? 'chat.artifacts.viewer.code' : 'chat.artifacts.viewer.page')}
            </button>
          ))}
        </div>
      ) : null}
      <div className="tabbio-artifact-panel-body">
        {current === 'code' ? (
          <DraftCodeView code={draft.code} title={title} />
        ) : (
          <DraftSnapshotFrame
            html={draft.snapshotHtml}
            version={draft.snapshotCount}
            title={title}
            crossfadeMs={motion.crossfadeMs}
            placeholder={
              isPage ? (
                <PageSkeleton draft={draft} shimmer={motion.shimmer} />
              ) : (
                <DocumentSkeleton draft={draft} shimmer={motion.shimmer} />
              )
            }
          />
        )}
      </div>
    </>
  );
}

type FrameLayer = { version: number; html: string; shown: boolean };

/**
 * The snapshot in the sandboxed preview frame. A new snapshot loads in a
 * second frame over the first and fades in once loaded; the older frame is
 * dropped after the crossfade. The skeleton stays under the frames until the
 * first one has loaded.
 */
function DraftSnapshotFrame({
  html,
  version,
  title,
  crossfadeMs,
  placeholder,
}: {
  html: string | null;
  version: number;
  title: string;
  crossfadeMs: number;
  placeholder: ReactNode;
}) {
  const { t } = useTranslation('common');
  const [layers, setLayers] = useState<FrameLayer[]>([]);

  useEffect(() => {
    if (!html) return;
    setLayers((previous) =>
      previous.some((layer) => layer.version === version)
        ? previous
        : [...previous.filter((layer) => layer.shown).slice(-1), { version, html, shown: false }],
    );
  }, [html, version]);

  const show = (shownVersion: number) => {
    setLayers((previous) =>
      previous.map((layer) => (layer.version === shownVersion ? { ...layer, shown: true } : layer)),
    );
  };

  // Once the newest frame is shown, the older one goes after the crossfade.
  const newest = layers[layers.length - 1];
  useEffect(() => {
    if (!newest?.shown || layers.length < 2) return;
    const timer = setTimeout(
      () => setLayers((previous) => previous.filter((layer) => layer.version >= newest.version)),
      crossfadeMs,
    );
    return () => clearTimeout(timer);
  }, [crossfadeMs, layers.length, newest?.shown, newest?.version]);

  const anyShown = layers.some((layer) => layer.shown);
  return (
    <div className="tabbio-draft-stage">
      {anyShown ? null : placeholder}
      {layers.map((layer) => (
        <iframe
          key={layer.version}
          srcDoc={layer.html}
          sandbox=""
          referrerPolicy="no-referrer"
          title={t('chat.artifacts.viewer.frameTitle', { title })}
          className={`tabbio-artifact-panel-frame tabbio-draft-frame${layer.shown ? ' is-shown' : ''}`}
          onLoad={() => show(layer.version)}
        />
      ))}
    </div>
  );
}

/** A page's skeleton: header, hero, one block per section so far (at least two), footer. */
function PageSkeleton({ draft, shimmer }: { draft: ArtifactDraft; shimmer: boolean }) {
  const sections = skeletonSectionCount(draft.stats);
  const block = (kind: string, key?: number) => (
    <div key={key} className={`tabbio-draft-block is-${kind}${shimmer ? ' is-shimmering' : ''}`} />
  );
  return (
    <div className="tabbio-draft-skeleton" aria-hidden>
      <div className="tabbio-draft-skeleton-page">
        {block('header')}
        {block('hero')}
        {Array.from({ length: sections }, (_, index) => block('section', index))}
        {block('footer')}
      </div>
    </div>
  );
}

/** A document's skeleton: a sheet with title bars, then each heading so far over three lines. */
function DocumentSkeleton({ draft, shimmer }: { draft: ArtifactDraft; shimmer: boolean }) {
  const line = (kind: string, key?: number) => (
    <div key={key} className={`tabbio-draft-line is-${kind}${shimmer ? ' is-shimmering' : ''}`} />
  );
  return (
    <div className="tabbio-draft-skeleton">
      <div className="tabbio-draft-sheet">
        <div aria-hidden>
          {line('title')}
          {line('subtitle')}
        </div>
        {draft.outline.map((entry, index) => (
          <section key={index} className="tabbio-draft-sheet-section">
            <h3 className="tabbio-draft-sheet-heading" dir="auto">
              {entry.heading}
            </h3>
            <div aria-hidden>{[0, 1, 2].map((key) => line('body', key))}</div>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * The page's source as it streams, in CodeMirror (lazy). It follows the end
 * while the reader is there; once they scroll up, a "Jump to latest" pill
 * brings them back. Never a live region.
 */
function DraftCodeView({ code, title }: { code: string; title: string }) {
  const { t } = useTranslation('common');
  const host = useRef<HTMLDivElement | null>(null);
  const editor = useRef<ReadOnlyCodeView | null>(null);
  const latest = useRef(code);
  latest.current = code;
  const following = useRef(true);
  const [ready, setReady] = useState(false);
  const [atEnd, setAtEnd] = useState(true);
  const label = t('chat.artifacts.building.codeLabel', { title });
  const labelRef = useRef(label);
  labelRef.current = label;

  useEffect(() => {
    let alive = true;
    let view: ReadOnlyCodeView | null = null;
    void import('./artifact-codemirror.web').then((codemirror) => {
      if (!alive || !host.current) return;
      view = codemirror.createReadOnlyHtmlView(host.current, latest.current, {
        label: labelRef.current,
        onScroll: (end) => {
          following.current = end;
          setAtEnd(end);
        },
      });
      editor.current = view;
      view.scrollToEnd();
      setReady(true);
    });
    return () => {
      alive = false;
      view?.destroy();
      editor.current = null;
    };
  }, []);

  useEffect(() => {
    const view = editor.current;
    if (!view) return;
    const length = view.length();
    if (code.length > length) view.append(code.slice(length));
    else if (code.length < length) view.replace(code);
    if (following.current) view.scrollToEnd();
  }, [code, ready]);

  return (
    <div className="tabbio-draft-code">
      <div ref={host} className="tabbio-draft-code-host" dir="ltr" />
      {ready ? null : (
        <div className="tabbio-draft-code-loading" aria-hidden>
          <SpinnerGlyph color="var(--text-secondary)" size={20} />
        </div>
      )}
      {ready && !atEnd ? (
        <button
          type="button"
          className="tabbio-draft-jump"
          onClick={() => {
            following.current = true;
            setAtEnd(true);
            editor.current?.scrollToEnd();
          }}
        >
          {t('chat.artifacts.building.jumpToLatest')}
        </button>
      ) : null}
    </div>
  );
}
